/**
 * Hero Headline Word Rotator
 *
 * Displays "Never forget another [word]." where [word] rotates through
 * occasion types (birthday, anniversary, milestone) with a smooth
 * slide/fade animation. Each word has its own accent color.
 *
 * The full sentence exists as a static H1 in the DOM for SEO crawlers.
 * The visual rotation is purely cosmetic — screen readers and crawlers
 * see the complete sentence with all occasions listed.
 *
 * Animation: the rotating word slides up and fades out, the next word
 * slides up from below and fades in. ~3s hold per word.
 */

import { useState, useEffect, useCallback } from 'react';
import { Title, rem } from '@mantine/core';
import { semanticColors, brandFonts } from '../../config/theme';

/** Duration (ms) each word is fully visible before transitioning out */
const DISPLAY_DURATION_MS = 3000;

/** Duration (ms) of the enter/exit CSS transition */
const TRANSITION_DURATION_MS = 500;

type AnimationPhase = 'entering' | 'visible' | 'exiting';

export interface RotatorWord {
  /** The occasion word to display (e.g., "birthday.") */
  text: string;
  /** CSS color value for this word */
  color: string;
}

interface HeadlineRotatorProps {
  /** Static prefix before the rotating word */
  prefix: string;
  /** Optional static suffix after the rotating word (e.g. "on autopilot.") */
  suffix?: string;
  /** Array of words to cycle through */
  words: readonly RotatorWord[];
  /** Full H1 text for SEO (visually hidden, always in DOM) */
  seoH1: string;
}

/**
 * Renders the hero headline with a rotating occasion word.
 *
 * The visible headline shows "Never forget another [birthday]." with the
 * last word cycling. A visually-hidden H1 contains the full static text
 * for crawlers. Optional `suffix` supports campaign copy like
 * "Put every [birthday] on autopilot."
 */
export const HeadlineRotator = ({ prefix, suffix, words, seoH1 }: HeadlineRotatorProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [phase, setPhase] = useState<AnimationPhase>('entering');

  const advanceWord = useCallback(() => {
    setPhase('exiting');

    setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % words.length);
      setPhase('entering');
    }, TRANSITION_DURATION_MS);
  }, [words.length]);

  useEffect(() => {
    if (phase === 'entering') {
      const enterTimer = setTimeout(() => setPhase('visible'), TRANSITION_DURATION_MS);
      return () => clearTimeout(enterTimer);
    }

    if (phase === 'visible') {
      const displayTimer = setTimeout(advanceWord, DISPLAY_DURATION_MS);
      return () => clearTimeout(displayTimer);
    }
  }, [phase, advanceWord]);

  const current = words[currentIndex];

  const getWordStyles = (): React.CSSProperties => {
    const base: React.CSSProperties = {
      display: 'inline-block',
      color: current.color,
      transition: [
        `transform ${TRANSITION_DURATION_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`,
        `opacity ${TRANSITION_DURATION_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`,
      ].join(', '),
    };

    switch (phase) {
      case 'entering':
        return { ...base, transform: 'translateY(40%)', opacity: 0 };
      case 'visible':
        return { ...base, transform: 'translateY(0)', opacity: 1 };
      case 'exiting':
        return { ...base, transform: 'translateY(-40%)', opacity: 0 };
    }
  };

  return (
    <>
      {/* SEO-stable H1 — visually hidden but always in DOM for crawlers */}
      <Title
        order={1}
        c={semanticColors.text.title}
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          borderWidth: 0,
        }}
      >
        {seoH1}
      </Title>

      {/* Visual headline with rotating word */}
      <Title
        order={2}
        aria-hidden="true"
        c={semanticColors.text.title}
        style={{ fontSize: rem(52), letterSpacing: -1, lineHeight: 1.15, fontFamily: brandFonts.headline }}
      >
        {prefix}{' '}
        <span style={{ display: 'inline-block', overflow: 'hidden', verticalAlign: 'bottom' }}>
          <span style={{ ...getWordStyles(), fontWeight: 800 }}>
            {current.text}
          </span>
        </span>
        {suffix ? <>{' '}{suffix}</> : null}
      </Title>
    </>
  );
};
